import { CartItem, getCartItems } from '../reducers/checkout.reducer';
import { CartItemFile, getCartItemFile } from '../utils/checkout-utils';
import { Injectable, inject } from '@angular/core';
import { RootReducer, Store } from '@app/app.reducers';
import { ProductFlowPage } from '@app/product/reducers/product-flow.reducer';
import { ProductType } from '@app/product/reducers/product.reducer';
import { hasUserProvidedPrintfile, productTypeMatches } from '@app/product/utils/product-utils';
import { FileUploadStatus, getUploads } from '@app/shared/reducers/upload.reducer';
import { castArray } from '@app/shared/utils/util';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export const enum PrintfileState {
	Error = -1,
	Warning = 0,
	Processing = 1,
	Ok = 2,
}

/** Validator service for printfiles. */
@Injectable({ providedIn: 'root' })
export class PrintfileValidatorService {
	private readonly store = inject<Store<RootReducer.State>>(Store);

	private isInitialized = false;
	private uploads: Record<string, FileUploadStatus> = {};

	/** Initialize subscriptions for upload status and cart item error sync. */
	init(): void {
		if (this.isInitialized) return;
		this.isInitialized = true;
		this.store
			.select(getUploads)
			.pipe(distinctUntilChanged())
			.subscribe((uploads) => {
				this.uploads = uploads;
			});
	}

	/** Observable indicating whether all cart items have acceptable printfiles. */
	arePrintfilesValid$(isWarningOk = false): Observable<boolean> {
		return this.store
			.select(getCartItems)
			.pipe(
				map((items) => items.map((item) => this.hasValidPrintfile(item, isWarningOk)).every(Boolean)),
			);
	}

	/** Check if a single cart item has a valid printfile. */
	hasValidPrintfile(item: api.CartItemWithProductDto, isWarningOk = false): boolean {
		// Printfiles that have "premedia fix" requested are always considered ok
		const isPremediaFixRequested = item?.options?.selectedOptions?.is_premedia_fix_requested === true;
		if (isPremediaFixRequested) return true;
		const fileState = this.getCartItemFileState(item);
		if (fileState === PrintfileState.Ok) return true;
		return isWarningOk && fileState === PrintfileState.Warning;
	}

	/** Determine the file state for a cart item based on product type and workflow. */
	getCartItemFileState(item: CartItem): PrintfileState {
		const product = item?.product;
		// Only "variable" products need to have a valid printfile
		if (!hasUserProvidedPrintfile(product)) return PrintfileState.Ok;

		const printfile = getCartItemFile(item);
		if (!printfile) {
			return PrintfileState.Error;
		}

		if (productTypeMatches(product, ProductType.MultipleOption)) {
			// Multiple option products may require an uploaded editor state or files
			return this.getMultipleOptionFileState(item, printfile);
		}

		return this.getPrintfileReportState(item, printfile);
	}

	/** Compute file state for multiple option products depending on workflow. */
	private getMultipleOptionFileState(item: CartItem, printfile: CartItemFile): PrintfileState {
		const workflow = item?.productFlowData?.selectedWorkflow?.key;
		switch (workflow) {
			case ProductFlowPage.Upload:
			case ProductFlowPage.ProductDesigner:
				return this.getPrintfileReportState(item, printfile as api.PrintfileDto);
			case ProductFlowPage.DesignService:
				return castArray(printfile)
					.map((file) => {
						const uploadStatus = this.uploads[file.key];
						return !uploadStatus || uploadStatus === FileUploadStatus.Success;
					})
					.every(Boolean)
					? PrintfileState.Ok
					: PrintfileState.Error;
			default:
				return PrintfileState.Ok; // Workflow does not require printfile
		}
	}

	/** Translate printfile report into a printable state with warning/error handling. */
	private getPrintfileReportState(item: CartItem, printfile: CartItemFile): PrintfileState {
		if (!printfile) return PrintfileState.Error;
		const isUploading = this.uploads[printfile?.key] === FileUploadStatus.Uploading;
		const isProcessing = item.isProcessing;
		if (isUploading || isProcessing) return PrintfileState.Processing;
		const isPremediaFixRequested = item?.options?.selectedOptions?.is_premedia_fix_requested ?? false;
		const hasReport = printfile && 'report' in printfile;

		// File infra V2 case
		if (
			hasReport &&
			printfile.report &&
			'status' in printfile.report &&
			printfile.report?.status === 'ERROR'
		)
			return PrintfileState.Error;

		const hasErrors =
			hasReport && Array.isArray(printfile.report?.errors) && printfile.report.errors.length > 0;
		const hasWarnings =
			hasReport && Array.isArray(printfile.report?.warnings) && printfile.report.warnings.length > 0;
		if (hasErrors && !isPremediaFixRequested) return PrintfileState.Error;
		if (hasWarnings || isPremediaFixRequested) return PrintfileState.Warning;

		return PrintfileState.Ok;
	}
}
